import { useState, useCallback, useRef } from 'react';
import apiClient from '../services/apiClient';
import { extractEndpointQuality } from '../utils/kpiQualityContract';

export const TASK_KPI_ENDPOINTS = {
    completionRate: {
        id: 'completion-rate',
        path: (projectId) => `/tasks/completion-rate/${projectId}/`,
        filters: (projectId) => ({ project_id: projectId }),
        sourceTag: 'tasks/completion-rate',
    },
    stuckTasks: {
        id: 'stuck-tasks',
        path: (projectId) => `/tasks/stuck-tasks/${projectId}/`,
        filters: (projectId) => ({ project_id: projectId }),
        sourceTag: 'tasks/stuck-tasks',
    },
    completionTrends: {
        id: 'completion-trends',
        path: (projectId) => `/tasks/completion-trends/${projectId}/`,
        filters: (projectId) => ({ project_id: projectId }),
        sourceTag: 'tasks/completion-trends',
    },
    estimationAccuracy: {
        id: 'estimation-accuracy',
        path: (employeeId) => `/tasks/estimation-accuracy/${employeeId}/`,
        filters: (employeeId) => ({ employee_id: employeeId }),
        sourceTag: 'tasks/estimation-accuracy',
    },
    estimationSuggestions: {
        id: 'estimation-suggestions',
        path: (employeeId) => `/tasks/estimation-suggestions/${employeeId}/`,
        filters: (employeeId) => ({ employee_id: employeeId }),
        sourceTag: 'tasks/estimation-suggestions',
    },
    teamProductivityDashboard: {
        id: 'team-productivity-dashboard',
        path: (departmentId) => `/tasks/team-productivity-dashboard/${departmentId}/`,
        filters: (departmentId) => ({ department_id: departmentId }),
        sourceTag: 'tasks/team-productivity-dashboard',
    },
    teamEstimationTrends: {
        id: 'team-estimation-trends',
        path: (departmentId) => `/tasks/team-estimation-trends/${departmentId}/`,
        filters: (departmentId) => ({ department_id: departmentId }),
        sourceTag: 'tasks/team-estimation-trends',
    },
};

const _getFailureQualityPayload = (error, fallbackMessage) => ({
    status: error?.response?.status || 500,
    data: {
        success: false,
        error: error?.response?.data?.error || error?.message || fallbackMessage,
        ...(error?.response?.data || {}),
    },
});

/**
 * Custom hook for managing tasks data and operations
 * Handles task fetching, caching, and project-specific task loading
 */
export const useTasks = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [taskEndpointLoading, setTaskEndpointLoading] = useState({});
    const [taskEndpointError, setTaskEndpointError] = useState({});
    const [taskEndpointQuality, setTaskEndpointQuality] = useState({});

    // Cache for tasks by project to avoid repeated API calls
    const [tasksCache, setTasksCache] = useState({});

    // Track the latest in-flight request key to avoid race conditions
    const latestRequestKeyRef = useRef(null);

    const setEndpointMeta = useCallback((key, updates = {}) => {
        if (!key) {
            return;
        }

        setTaskEndpointLoading((previous) => ({
            ...previous,
            [key]: typeof updates.loading === 'boolean' ? updates.loading : previous[key],
        }));
        setTaskEndpointError((previous) => ({
            ...previous,
            [key]: updates.error !== undefined ? updates.error : previous[key],
        }));
        setTaskEndpointQuality((previous) => ({
            ...previous,
            [key]: updates.quality || previous[key],
        }));
    }, []);

    const fetchTasks = useCallback(async (projectId = null, forceRefresh = false) => {
        // Namespace cache key to avoid collisions
        const cacheKey = projectId ? `project:${projectId}` : 'all_tasks';

        // Check cache first unless force refresh is requested
        if (!forceRefresh && tasksCache[cacheKey]) {
            const cachedTasks = tasksCache[cacheKey];
            // Check if cache is still valid (5 minutes)
            if (Date.now() - cachedTasks.timestamp < 300000) {
                setTasks(cachedTasks.data);
                return cachedTasks.data;
            }
        }

        try {
            setLoading(true);
            setError(null);

            console.log('Fetching tasks for:', projectId ? `project ${projectId}` : 'all tasks');

            const params = {
                page_size: 100,
                ordering: 'due_date'
            };

            // Add project filter only if projectId is provided
            if (projectId) {
                params.project = projectId;
                params.ordering = 'name'; // Different ordering for project-specific tasks
            }

            console.log('API call params:', params);

            // Mark this request as the latest
            latestRequestKeyRef.current = cacheKey;
            const requestKeyAtStart = cacheKey;

            let allTasks = [];
            let nextUrl = '/tasks/';
            let page = 1;

            while (nextUrl) {
                console.log('Fetching page ' + page + ' for ' + (projectId ? 'project ' + projectId : 'all tasks'));
                const response = await apiClient.get(nextUrl, {
                    params: projectId ? params : { ...params, page: page }
                });

                console.log('Response for page ' + page + ':', response.data);

                const tasksData = Array.isArray(response.data.results)
                    ? response.data.results
                    : Array.isArray(response.data) ? response.data : [];

                console.log('Tasks from this page:', tasksData.length);

                allTasks = [...allTasks, ...tasksData];

                nextUrl = response.data.next;
                page++;
            }

            console.log('Total fetched tasks:', allTasks.length, 'for', projectId ? 'project ' + projectId : 'all');

            // Only update state if this response is for the latest request to avoid race overwrites
            if (latestRequestKeyRef.current === requestKeyAtStart) {
                // Update cache
                setTasksCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: allTasks,
                        timestamp: Date.now()
                    }
                }));

                setTasks(allTasks);
            } else {
                console.log('Stale tasks response ignored for key:', requestKeyAtStart);
            }

            return allTasks;

        } catch (err) {
            console.error('Error fetching tasks:', err);
            setError(err.response?.data?.detail || 'Failed to load tasks');
            // Do not clear current tasks if this was a stale or non-latest request
            if (latestRequestKeyRef.current === cacheKey) {
                // Keep existing tasks; return empty for caller
                // setTasks([]);
            }
            return [];
        } finally {
            setLoading(false);
        }
    }, [tasksCache]);

    const createTask = useCallback(async (taskData) => {
        try {
            setError(null);
            const response = await apiClient.post('/tasks/', taskData);

            // Update tasks if they're for the same project
            if (tasks.length > 0 && tasks[0]?.project === taskData.project) {
                setTasks(prev => [...prev, response.data]);
            }

            // Update cache for project-specific tasks
            const cacheKey = taskData.project ? `project:${taskData.project}` : null;
            if (cacheKey && tasksCache[cacheKey]) {
                setTasksCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: [...(prev[cacheKey]?.data || []), response.data],
                        timestamp: Date.now()
                    }
                }));
            }

            // Invalidate all_tasks cache since we added a new task
            setTasksCache(prev => {
                const newCache = { ...prev };
                delete newCache.all_tasks;
                return newCache;
            });

            return { success: true, data: response.data };
        } catch (err) {
            console.error('Error creating task:', err);
            setError(err.response?.data?.detail || 'Failed to create task');
            return {
                success: false,
                error: err.response?.data?.detail || 'Failed to create task'
            };
        }
    }, [tasks, tasksCache]);

    const updateTask = useCallback(async (taskId, taskData) => {
        try {
            setError(null);
            const response = await apiClient.put(`/tasks/${taskId}/`, taskData);

            // Update tasks if the task is in current list
            setTasks(prev => prev.map(task =>
                task.id === taskId ? response.data : task
            ));

            // Update cache for the project
            setTasksCache(prevCache => {
                const newCache = { ...prevCache };
                Object.entries(newCache).forEach(([key, cacheEntry]) => {
                    if (!cacheEntry?.data) return;
                    newCache[key] = {
                        ...cacheEntry,
                        data: cacheEntry.data.map(task => task.id === taskId ? response.data : task)
                    };
                });
                return newCache;
            });

            return { success: true, data: response.data };
        } catch (err) {
            console.error('Error updating task:', err);
            return {
                success: false,
                error: err.response?.data?.detail || 'Failed to update task'
            };
        }
    }, []);

    const patchTask = useCallback(async (taskId, updates) => {
        try {
            setError(null);
            const response = await apiClient.patch(`/tasks/${taskId}/`, updates);
            const updatedTask = response.data;

            setTasks(prev => prev.map(task =>
                task.id === taskId ? updatedTask : task
            ));

            setTasksCache(prevCache => {
                const newCache = { ...prevCache };
                Object.entries(newCache).forEach(([key, cacheEntry]) => {
                    if (!cacheEntry?.data) return;
                    newCache[key] = {
                        ...cacheEntry,
                        data: cacheEntry.data.map(task => task.id === taskId ? updatedTask : task)
                    };
                });
                return newCache;
            });

            return { success: true, data: updatedTask };
        } catch (err) {
            console.error('Error patching task:', err);
            const message = err.response?.data?.detail || err.message || 'Failed to update task';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    const deleteTask = useCallback(async (taskId) => {
        try {
            setError(null);
            await apiClient.delete(`/tasks/${taskId}/`);

            // Find which project this task belongs to and update cache
            const taskToDelete = tasks.find(task => task.id === taskId);

            // Update tasks
            setTasks(prev => prev.filter(task => task.id !== taskId));

            // Update cache
            const cacheKey = taskToDelete?.project ? `project:${taskToDelete.project}` : null;
            if (cacheKey && tasksCache[cacheKey]) {
                setTasksCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: prev[cacheKey].data.filter(task => task.id !== taskId),
                        timestamp: Date.now()
                    }
                }));
            }

            // Invalidate all_tasks cache since we deleted a task
            setTasksCache(prev => {
                const newCache = { ...prev };
                delete newCache.all_tasks;
                return newCache;
            });

            return { success: true };
        } catch (err) {
            console.error('Error deleting task:', err);
            setError(err.response?.data?.detail || 'Failed to delete task');
            return {
                success: false,
                error: err.response?.data?.detail || 'Failed to delete task'
            };
        }
    }, [tasks, tasksCache]);

    const clearTasksCache = useCallback((projectId) => {
        if (projectId) {
            const cacheKey = `project:${projectId}`;
            setTasksCache(prev => {
                const newCache = { ...prev };
                delete newCache[cacheKey];
                return newCache;
            });
        } else {
            setTasksCache({});
        }
    }, []);

    const getTasksForProject = useCallback((projectId) => {
        if (!projectId) return [];
        const cacheKey = `project:${projectId}`;
        return tasksCache[cacheKey]?.data || [];
    }, [tasksCache]);

    /**
     * Fetch eligible users for task assignment
     * @param {Object} task - Task object containing project information
     * @param {Object} user - Current user for permission checks
     */
    const fetchEligibleUsers = useCallback(async (task, user) => {
        if (!task || !user) {
            return { success: false, users: [], error: 'Missing task or user information' };
        }

        const canAssignTasks = user?.role === 'project_manager' ||
            user?.role === 'admin' ||
            user?.is_superuser;

        if (!canAssignTasks) {
            return { success: false, users: [], error: 'You do not have permission to assign tasks' };
        }

        try {
            setError(null);

            // Get project details for context (not used but kept for future reference)
            await apiClient.get(`/projects/${task.project}/`);

            // Get all eligible users
            const usersResponse = await apiClient.get('/users/?for_task_assignment=true');

            // Industry Standard: Allow task assignment to any eligible user, not just project team members
            // Project team members are for resource planning, task assignments can include external specialists
            const eligibleUsers = usersResponse.data.results.filter(user => {
                const hasValidRole = ['employee', 'team_leader', 'project_manager'].includes(user.role);
                return hasValidRole;
            });

            if (eligibleUsers.length === 0) {
                return {
                    success: false,
                    users: [],
                    error: 'No eligible users found for task assignment.'
                };
            }

            return { success: true, users: eligibleUsers, error: null };
        } catch (err) {
            console.error('Error fetching eligible users:', err);
            const errorMessage = err.response?.data?.error || 'Failed to fetch users. Please try again.';
            setError(errorMessage);
            return { success: false, users: [], error: errorMessage };
        }
    }, []);

    /**
     * Assign task to a user
     * @param {Object} task - Task to assign
     * @param {number} userId - ID of user to assign task to
     * @param {Object} user - Current user for permission checks
     */
    const assignTask = useCallback(async (task, userId, user) => {
        if (!userId) {
            const errorMessage = 'Please select a team member to assign the task to';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }

        const canAssignTasks = user?.role === 'project_manager' ||
            user?.role === 'admin' ||
            user?.is_superuser;

        if (!canAssignTasks) {
            const errorMessage = 'You do not have permission to assign tasks';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }

        try {
            setError(null);
            const response = await apiClient.post(`/tasks/${task.id}/assign/`, {
                user_id: parseInt(userId)
            });

            // Update tasks if the task is in current list
            setTasks(prev => prev.map(t =>
                t.id === task.id ? response.data : t
            ));

            // Update cache for the project
            const cacheKey = task.project ? `project:${task.project}` : null;
            if (cacheKey && tasksCache[cacheKey]) {
                setTasksCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: prev[cacheKey].data.map(t =>
                            t.id === task.id ? response.data : t
                        ),
                        timestamp: Date.now()
                    }
                }));
            }

            return { success: true, data: response.data, error: null };
        } catch (error) {
            console.error('Error assigning task:', error);
            const errorMessage = error.response?.data?.error || 'Failed to assign task. Please try again.';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [tasksCache]);

    /**
     * Assign task to multiple users (Industry Standard PSA Feature)
     * @param {number} taskId - ID of task to assign
     * @param {Array} userIds - Array of user IDs to assign task to
     */
    const assignTaskToUsers = useCallback(async (taskId, userIds, action = 'replace') => {
        try {
            setError(null);
            const response = await apiClient.post(`/tasks/${taskId}/assign/`, {
                assigned_user_ids: userIds || [],
                action: action // 'add', 'remove', or 'replace'
            });

            // Update tasks if the task is in current list
            // Handle both single task response and success response with task data
            const updatedTask = response.data.task || response.data;
            setTasks(prev => prev.map(t =>
                t.id === taskId ? updatedTask : t
            ));

            // Update cache for the project
            const taskToUpdate = tasks.find(t => t.id === taskId);
            const cacheKey = taskToUpdate?.project ? `project:${taskToUpdate.project}` : null;
            if (cacheKey && tasksCache[cacheKey]) {
                setTasksCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: prev[cacheKey].data.map(t =>
                            t.id === taskId ? updatedTask : t
                        ),
                        timestamp: Date.now()
                    }
                }));
            }

            return { success: true, data: response.data, error: null };
        } catch (error) {
            console.error('Error assigning task to users:', error);
            const errorMessage = error.response?.data?.error || 'Failed to assign task. Please try again.';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [tasks, tasksCache]);

    // ==================== PHASE 2.1: COMPLETION RATE TRACKING ====================
    const [completionRateData, setCompletionRateData] = useState(null);

    const getCompletionRateForProject = useCallback(async (projectId) => {
        const endpointConfig = TASK_KPI_ENDPOINTS.completionRate;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get(endpointConfig.path(projectId));
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(projectId)
            );
            setCompletionRateData(response.data);
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });
            return response.data;
        } catch (err) {
            console.error('Error fetching completion rate:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load completion rate data'),
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(projectId)
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load completion rate data',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load completion rate data');
            return null;
        } finally {
            setLoading(false);
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    const getStuckTasks = useCallback(async (projectId) => {
        const endpointConfig = TASK_KPI_ENDPOINTS.stuckTasks;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            const response = await apiClient.get(endpointConfig.path(projectId));

            // Merge stuck tasks into completion rate data
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(projectId)
            );
            setCompletionRateData(prev => ({
                ...prev,
                stuck_tasks: Array.isArray(response.data?.stuck_tasks)
                    ? response.data.stuck_tasks
                    : [],
            }));
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });

            return response.data;
        } catch (err) {
            console.error('Error fetching stuck tasks:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load stuck tasks data'),
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(projectId)
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load stuck tasks data',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load stuck tasks data');
            return [];
        } finally {
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    const getCompletionRateTrends = useCallback(async (projectId, months = 6) => {
        const endpointConfig = TASK_KPI_ENDPOINTS.completionTrends;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            const response = await apiClient.get(endpointConfig.path(projectId), {
                params: { months }
            });

            // Merge trends into completion rate data
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                {
                    ...endpointConfig.filters(projectId),
                    months,
                }
            );
            setCompletionRateData(prev => ({
                ...prev,
                completion_trends: Array.isArray(response.data?.completion_trends)
                    ? response.data.completion_trends
                    : [],
            }));
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });

            return response.data;
        } catch (err) {
            console.error('Error fetching completion trends:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load completion trends'),
                endpointKey,
                endpointConfig.sourceTag,
                {
                    ...endpointConfig.filters(projectId),
                    months,
                }
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load completion trends',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load completion trends');
            return [];
        } finally {
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    // ==================== PHASE 2.2: ESTIMATION ACCURACY ====================
    const [estimationAccuracyData, setEstimationAccuracyData] = useState(null);

    const getEstimationAccuracyForEmployee = useCallback(async (employeeId) => {
        const endpointConfig = TASK_KPI_ENDPOINTS.estimationAccuracy;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            const response = await apiClient.get(endpointConfig.path(employeeId));
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(employeeId)
            );
            setEstimationAccuracyData(response.data);
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });
            return response.data;
        } catch (err) {
            console.error('Error fetching estimation accuracy:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load estimation accuracy data'),
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(employeeId)
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load estimation accuracy data',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load estimation accuracy data');
            return null;
        } finally {
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    const getEstimationImprovementSuggestions = useCallback(async (employeeId) => {
        const endpointConfig = TASK_KPI_ENDPOINTS.estimationSuggestions;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            const response = await apiClient.get(endpointConfig.path(employeeId));
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(employeeId)
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });
            return response.data;
        } catch (err) {
            console.error('Error fetching estimation suggestions:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load estimation suggestions'),
                endpointKey,
                endpointConfig.sourceTag,
                endpointConfig.filters(employeeId)
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load estimation suggestions',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load estimation suggestions');
            return [];
        } finally {
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    // ==================== PHASE 2.4: TEAM PRODUCTIVITY DASHBOARD ====================
    const [teamProductivityData, setTeamProductivityData] = useState(null);

    const getTeamProductivityDashboardData = useCallback(async (departmentId, timePeriod = 'last_30_days') => {
        const endpointConfig = TASK_KPI_ENDPOINTS.teamProductivityDashboard;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get(endpointConfig.path(departmentId), {
                params: { period: timePeriod }
            });
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                {
                    ...endpointConfig.filters(departmentId),
                    period: timePeriod,
                }
            );
            setTeamProductivityData(response.data);
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });
            return response.data;
        } catch (err) {
            console.error('Error fetching team productivity data:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load team productivity data'),
                endpointKey,
                endpointConfig.sourceTag,
                {
                    ...endpointConfig.filters(departmentId),
                    period: timePeriod,
                }
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load team productivity data',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load team productivity data');
            return null;
        } finally {
            setLoading(false);
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    const getTeamEstimationAccuracyTrends = useCallback(async (departmentId, months = 6) => {
        const endpointConfig = TASK_KPI_ENDPOINTS.teamEstimationTrends;
        const endpointKey = endpointConfig.id;

        setEndpointMeta(endpointKey, {
            loading: true,
            error: null,
        });
        try {
            const response = await apiClient.get(endpointConfig.path(departmentId), {
                params: { months }
            });
            const quality = extractEndpointQuality(
                response,
                endpointKey,
                endpointConfig.sourceTag,
                {
                    ...endpointConfig.filters(departmentId),
                    months,
                }
            );

            // Merge trends into team productivity data
            setTeamProductivityData(prev => ({
                ...prev,
                estimation_accuracy_trends: response.data
            }));
            setEndpointMeta(endpointKey, {
                loading: false,
                error: null,
                quality,
            });

            return response.data;
        } catch (err) {
            console.error('Error fetching team estimation trends:', err);
            const failureQuality = extractEndpointQuality(
                _getFailureQualityPayload(err, 'Failed to load team estimation trends'),
                endpointKey,
                endpointConfig.sourceTag,
                {
                    ...endpointConfig.filters(departmentId),
                    months,
                }
            );
            setEndpointMeta(endpointKey, {
                loading: false,
                error: failureQuality.warnings?.[0] || 'Failed to load team estimation trends',
                quality: failureQuality,
            });
            setError(failureQuality.warnings?.[0] || err.response?.data?.detail || 'Failed to load team estimation trends');
            return [];
        } finally {
            setEndpointMeta(endpointKey, {
                loading: false,
            });
        }
    }, [setEndpointMeta]);

    return {
        tasks,
        loading,
        error,
        taskEndpointLoading,
        taskEndpointError,
        taskEndpointQuality,
        fetchTasks,
        createTask,
        updateTask,
        patchTask,
        deleteTask,
        clearTasksCache,
        getTasksForProject,
        setTasks,
        // Task assignment operations
        fetchEligibleUsers,
        assignTask,
        // Phase 2.1: Completion Rate Tracking
        completionRateData,
        getCompletionRateForProject,
        getStuckTasks,
        getCompletionRateTrends,
        // Phase 2.2: Estimation Accuracy 
        estimationAccuracyData,
        getEstimationAccuracyForEmployee,
        getEstimationImprovementSuggestions,
        // Phase 2.4: Team Productivity Dashboard
        teamProductivityData,
        getTeamProductivityDashboardData,
        getTeamEstimationAccuracyTrends,
        // Task assignment operations
        assignTaskToUsers
    };
}; 
